import svgChevronDown from '@static/icons/chevron-down.svg?raw';
import svgChevronUp from '@static/icons/chevron-up.svg?raw';
import svgEraser from '@static/icons/eraser.svg?raw';

import {
    toTitleCase,
    unixTimestampToReadableDate,
} from '@/js/util.js';

import {
    assert,
    assertNonemptyString,
    assertString,
    tryParseFloatStrict,
} from '@/js/util/asserts.js';

import {
    ASSET_NAME,
    AssetType,
    AttributeType
} from '@/js/util/shared_constants.js';


import { getAttributeObj } from '@/js/classes/attribute.js';
import { getSpeciesData } from '@/js/util/shared_functionality.js';
import { convertDbhFromStoredToDisplay } from '@/js/util/tree_functionality.js';


class AssetFormCategory {
    constructor(name, form_elem) {
        assertNonemptyString(name);
        this.name = name;
        this.display_name = (name==='custom' ? 'Custom Fields' : toTitleCase(name));
        this.form_elem = form_elem;

        this.collapsed = false;
        this._has_cleared_once = false;
    }

    clear() {
        const skip_classes = '.prevent-clearing' + (this._has_cleared_once ? '' : ', .prevent-clearing-once');
        const category_selector = `.attribute[data-category="${this.name}"]:not(${skip_classes})`;
        const inputs = this.form_elem.querySelectorAll(`${category_selector} input, ${category_selector} textarea`);
        for(const input of inputs) {
            const input_tagname = input.tagName;

            if(input_tagname === 'INPUT') {
                if(input.disabled) { continue; }
                const input_type = input.type;
                if(input_type==='text' || input_type==='number' || input_type==='datetime-local') {
                    input.value = '';
                } else if(input_type==='radio' || input_type==='checkbox') {
                    input.checked = false;
                } else {
                    console.error('Unhandled input_type `'+input_type+'`', input);
                }

            } else if(input_tagname === 'TEXTAREA') {
                if(input.closest('[data-attribute-type="ExpandingButtonMultiOptionAttribute"]') !== null) {
                    input.classList.add('hidden');
                } else if(input.disabled) {
                    continue;
                }
                input.value = '';

            } else {
                console.error('Unhandled input_tagname `'+input_tagname+'`', input);
            }
        }

        const selects = this.form_elem.querySelectorAll(category_selector+' select');
        for(const select of selects) {
            if(select.disabled) { continue; }
            select.selectedIndex = 0;
        }

        const textareas = this.form_elem.querySelectorAll(category_selector+' textarea');
        for(const textarea of textareas) {
            if(textarea.disabled) { continue; }
            textarea.value = '';
        }

        this._has_cleared_once = true;
    }

    toggle() {
        if(this.collapsed) {
            this.expand();
        } else {
            this.collapse();
        }
    }

    expand() {
        const matching_elems = this.form_elem.querySelectorAll('.attribute[data-category="'+this.name+'"]');
        for(const matching_elem of matching_elems) { matching_elem.style.display = 'block'; }
        this.collapse_button_elem.innerHTML = svgChevronDown;
        this.heading_elem.classList.remove('opacity-50');
        this.collapsed = false;

    }

    collapse() {
        const matching_elems = this.form_elem.querySelectorAll('.attribute[data-category="'+this.name+'"]');
        for(const matching_elem of matching_elems) { matching_elem.style.display = 'none'; }
        this.collapse_button_elem.innerHTML = svgChevronUp;
        this.heading_elem.classList.add('opacity-50');
        this.collapsed = true;
    }

    show() {
        this.category_wrapper_elem.classList.remove('hidden');
    }

    hide() {
        this.category_wrapper_elem.classList.add('hidden');
    }

    generate() {
        const category_wrapper_elem = document.createElement('div');
        this.category_wrapper_elem = category_wrapper_elem;

        const heading_elem = document.createElement('div');
        heading_elem.classList.add('form-category-heading');
        this.heading_elem = heading_elem;
        category_wrapper_elem.appendChild(heading_elem);

        const heading_text_elem = document.createElement('span');
        heading_text_elem.classList.add('h4');
        heading_text_elem.innerText = this.display_name;
        heading_elem.appendChild(heading_text_elem);

        const button_wrapper_elem = document.createElement('div');
        button_wrapper_elem.classList.add('d-flex', 'gap-3');
        heading_elem.appendChild(button_wrapper_elem);

        /* Clear Button */ {
            const clear_button_elem = document.createElement('button');
            clear_button_elem.classList.add('clear-category', 'd-inline-flex', 'align-items-center', 'gap-1');
            clear_button_elem.type = 'button';
            clear_button_elem.title = 'Clear Category';
            clear_button_elem.innerHTML = svgEraser;
            button_wrapper_elem.appendChild(clear_button_elem);
            this.clear_button_elem = clear_button_elem;

            clear_button_elem.addEventListener('click', () => {
                this.clear();
            });

            const clear_button_text_elem = document.createElement('span');
            clear_button_text_elem.innerHTML = `Clear<span class="asset-form-clear-text-section"> Section</span>`;
            clear_button_elem.appendChild(clear_button_text_elem);
        }

        /* Collapse Button */ {
            const collapse_button_elem = document.createElement('button');
            collapse_button_elem.classList.add('collapse-category', 'flex-center');
            collapse_button_elem.type = 'button';
            collapse_button_elem.title = 'Collapse or Expand Category';
            collapse_button_elem.innerHTML = svgChevronDown;
            button_wrapper_elem.appendChild(collapse_button_elem);
            this.collapse_button_elem = collapse_button_elem;

            collapse_button_elem.addEventListener('click', () => {
                this.toggle();
            });
        }

        const category_body_elem = document.createElement('div');
        category_body_elem.classList.add('category-body');
        category_body_elem.setAttribute('data-category', this.name);
        this.category_body_elem = category_body_elem;
        category_wrapper_elem.appendChild(category_body_elem);

        return category_wrapper_elem;
    }

    showClearButton() {
        this.clear_button_elem.classList.remove('hidden');
    }

    hideClearButton() {
        this.clear_button_elem.classList.add('hidden');
    }

    showCollapseButton() {
        this.collapse_button_elem.classList.remove('hidden');
    }

    hideCollapseButton() {
        this.collapse_button_elem.classList.add('hidden');
    }
}


export class AssetForm {
    constructor(opts) {
        assert('form_elem' in opts);
        assert('attributes' in opts);

        this.form_elem = opts['form_elem'];
        this.attributes = opts['attributes'];
        this.values = opts['values'] ?? {};
        this.is_new = opts['is_new'] ?? false;
        this.files = opts['files'] ?? {};

        this.attribute_objs = {};
        this.category_objs = {};
    }

    validateForm() {
        let had_invalid_inputs = false;
        let values = {};
        let has_scrolled_to_invalid_elem = false;
        for(const attribute of this.attributes) {
            const attribute_obj = this.attribute_objs[attribute.internal_name];
            if(attribute.hidden || (attribute.readOnly && (!this.is_new))) {
                values[attribute.internal_name] = (this.values[attribute.internal_name] ?? '');

            } else {
                try {
                    const value = attribute_obj.validate();
                    attribute_obj.setValidationText('');
                    values[attribute.internal_name] = value;

                } catch(err) {
                    console.log('Form validation error: ['+attribute.internal_name+'] '+err.message);
                    attribute_obj.setValidationText(err.message);
                    had_invalid_inputs = true;
                    if(attribute.category_name !== '') {
                        const category = this.category_objs[attribute.category_name.toLowerCase()];
                        category.show();
                        category.expand();
                    }
                    if(!has_scrolled_to_invalid_elem) {
                        attribute_obj.scrollTo();
                        has_scrolled_to_invalid_elem = true;
                    }
                }
            }
        }
        if(had_invalid_inputs) { return false; }
        return values;
    }

    generateForm() {
        /* Add attributes, by category */ {
            let last_category_name = '____nonexistent_category____';
            for(const attribute of this.attributes) {
                if(attribute.hidden) { continue; }

                // Add category heading
                if(attribute.category_name !== last_category_name && attribute.category_name !== '') {
                    const category_obj = new AssetFormCategory(attribute.category_name, this.form_elem);
                    this.form_elem.appendChild(category_obj.generate());
                    last_category_name = attribute.category_name;
                    const category_internal_name = attribute.category_name.toLowerCase();
                    if(category_internal_name in this.category_objs) { console.log('Duplicate category name `'+category_internal_name+'`'); }
                    this.category_objs[category_internal_name] = category_obj;
                }

                // Add attribute
                const attribute_obj = getAttributeObj({
                    'attribute': attribute,
                    'value': this.values[attribute.internal_name],
                    'files': this.files,
                    'is_new': this.is_new,
                    'other_attributes': this.attributes,
                    'other_values': this.values,
                });
                this.attribute_objs[attribute.internal_name] = attribute_obj;
                this.form_elem.appendChild(attribute_obj.setup());

            }
        }
    }

    getAddedImages() {
        const added_images = {};
        for(const attribute of this.attributes) {
            if(attribute.attribute_type !== AttributeType.Image) { continue; }
            const attribute_obj = this.attribute_objs[attribute.internal_name];
            for(const [file_id, blob] of Object.entries(attribute_obj.images_added)) {
                added_images[file_id] = blob;
            }
        }
        return added_images;
    }

    hasCategory(category_name) {
        assertString(category_name);
        category_name = category_name.toLowerCase();
        return (category_name in this.category_objs);
    }

    getCategory(category_name) {
        assertString(category_name);
        category_name = category_name.toLowerCase();
        assert(category_name in this.category_objs,'Invalid category name `'+category_name+'`');
        return this.category_objs[category_name];
    }

    cleanup() {
        const images = this.form_elem.querySelectorAll('img');
        for(const img of images) {
            if(img.src && img.src.startsWith('blob:')) {
                URL.revokeObjectURL(img.src);
            }
        }
    }
}


export const displayAttribute = (value, attribute, project) => {
    const attribute_type = attribute.attribute_type;
    const attribute_internal_name = attribute.internal_name;
    if(!('settings' in window.newcollector)) { console.error('window.newcollector.settings not present'); return value; }


    if(attribute_internal_name === 'dbh') {
        if(tryParseFloatStrict(value)) {
            value = convertDbhFromStoredToDisplay(value);
        }
        return value;

    } else if(attribute_internal_name === 'leaf_type') {
        const species_attribute = project.getAttribute(AssetType.Tree, 'species');
        if(!(value in species_attribute.options)) { return value; }
        const species_data = getSpeciesData(species_attribute.options[value]);
        return species_data['leaf_type'];

    } else if(attribute_internal_name === 'asset_type') {
        return ASSET_NAME[value];

    } else if(attribute_type === AttributeType.Proportion) {
        return value[0] + ' of ' + value[1];

    } else if(attribute_type === AttributeType.Timestamp) {
        return unixTimestampToReadableDate(value);

    } else if(attribute_type === AttributeType.Date) {
        return unixTimestampToReadableDate(value, { 'dateOnly': true });

    } else if(attribute_type === AttributeType.SpeciesSearchDropdown) {
        if(!(value in attribute.options)) { return value; }
        const species_data = getSpeciesData(attribute.options[value]);
        return (species_data['common_name']+' | '+species_data['scientific_name']);

    } else if([AttributeType.KeyedSearchDropdown, AttributeType.KeyedSingleOption, AttributeType.KeyedButtonSingleOption].includes(attribute_type)) {
        if(!(value in attribute.options)) { return value; }
        return attribute.options[value];

    } else if(Array.isArray(value)) {
        return value.join(', ');

    } else {
        return value;

    }
};

export const sortAttribute = (value, attribute)=> {
    const attribute_type = attribute.attribute_type;

    if('sortByArrayIndex' in attribute && attribute.sortByArrayIndex===true) {
        const idx = attribute.options.indexOf(value);
        if(idx === -1) {
            console.error('Value `'+value+'` not found in options.');
            return 0;
        }
        return idx;

    } else if(attribute_type === AttributeType.SpeciesSearchDropdown) {
        if(!(value in attribute.options)) { return value; }
        const species_data = getSpeciesData(attribute.options[value]);
        return (species_data['common_name']+' | '+species_data['scientific_name']);

    } else {
        return value;
    }
};